<template>
  <section class="panel">
    <header class="panel-head">
      <h3><span class="fas fa-cart-shopping"></span>Achats <span class="count">{{ items.length }}</span></h3>
      <div class="head-actions">
        <button type="button" class="head-button primary" title="Rattacher un achat complet (facture de services, facture entière ou avoir) : sa part dans le dossier se saisit en montant, en % ou en quantité" @click="$refs.linkPurchaseModal.openModal({order, purchases})"><span class="fas fa-link"></span>Rattacher un achat</button>
        <button type="button" class="head-button" title="Rattacher des articles du catalogue : la quantité est prélevée sur le stock, sans dépendre du montant des factures d'achat" @click="$refs.assignArticlesModal.openModal({order})"><span class="fas fa-boxes-stacked"></span>Rattacher des articles</button>
        <button type="button" class="head-button" title="Créer un achat pour ce dossier" @click="$refs.purchaseModal.openModal(undefined, order.id)"><span class="fas fa-plus"></span>Nouvel achat</button>
      </div>
    </header>

    <div v-if="items.length > 0" class="panel-total">
      <span>Acheté sur ce dossier</span>
      <strong>{{ formatAmount(finance.bought) }} {{ finance.basis }}</strong>
    </div>

    <div v-if="orderArticles.length > 0" class="order-articles">
      <div class="order-articles-head">
        <strong>Articles du dossier</strong>
        <span>quantités prélevées sur le stock, déjà comprises dans les achats ci-dessous (sauf ce qui reste à approvisionner)</span>
      </div>
      <ul>
        <li v-for="entry in orderArticles" :key="entry.articleId">
          <span class="article-name" :title="entry.name">{{ entry.name }}</span>
          <span class="article-quantity">
            {{ formatQuantity(entry.quantity, entry.unit) }}
            <small v-if="entry.pendingQuantity > 0" class="pending" title="Affecté au-delà du stock : sans coût tant que l'achat n'est pas arrivé">+ {{ formatQuantity(entry.pendingQuantity, entry.unit) }} à approvisionner</small>
          </span>
          <span class="article-cost">{{ formatAmount(entry.costHt) }} HT</span>
          <button type="button" class="row-button danger" title="Retirer cet article du dossier : ses quantités retournent en stock" @click="removeArticle(entry)"><span class="fas fa-link-slash"></span>Retirer</button>
        </li>
      </ul>
    </div>

    <p v-if="items.length === 0" class="empty">Aucun achat n'est rattaché à ce dossier.<br>Rattachez les factures de vos fournisseurs, ou des articles du stock, pour suivre la marge réelle du dossier.</p>

    <article v-for="item in items" :key="item.purchase.id" class="purchase" :class="{review: item.isToReview, overdue: item.purchase.isOverdue}">
      <div class="purchase-body">
        <div class="purchase-info">
          <div class="purchase-id">
            <strong class="supplier">{{ item.purchase.supplierName || 'Fournisseur' }}</strong>
            <span class="purchase-label" :title="item.purchase.label">{{ purchaseTitle(item.purchase) }}</span>
          </div>
          <div class="purchase-meta">
            <StatusBadge :status="item.purchase.status" :status-enum="statusEnumOf(item.purchase)" />
            <span v-if="isCreditNote(item.purchase)" class="meta partial" title="Un avoir vient en déduction des achats du dossier">Avoir</span>
            <span class="meta" title="Date de la pièce">{{ formatDate(item.purchase.invoiceDate) }}</span>
            <span v-if="item.ratio < 0.995" class="meta partial" :title="'Montant total de l\'achat : ' + formatAmount(item.purchase.htAmount) + ' HT'">{{ Math.round(item.ratio * 100) }} % de l'achat</span>
            <span v-if="!item.isToReview && item.purchase.remainingAmount > 0" class="meta" :class="{late: item.purchase.isOverdue}" :title="item.purchase.isOverdue ? 'Échéance dépassée' : 'Reste à payer sur l\'achat (TTC)'">
              <span class="fas fa-hourglass-half"></span>
              <template v-if="isCreditNote(item.purchase)">Reste à recevoir {{ formatAmount(item.purchase.remainingAmount) }}</template>
              <template v-else>Reste {{ formatAmount(item.purchase.remainingAmount) }} · échéance {{ formatDate(item.purchase.dueDate) }}</template>
            </span>
            <span v-if="item.isToReview" class="meta warn"><span class="fas fa-triangle-exclamation"></span>À vérifier : pas encore compté dans la marge</span>
          </div>
        </div>
        <div class="purchase-side">
          <div class="purchase-share" :title="'Part de ce dossier dans ' + (isCreditNote(item.purchase) ? 'l\'avoir' : 'l\'achat') + (item.isTtc ? ' (TTC : le groupe ne récupère pas la TVA à cette date)' : ' (hors taxes)')">
            <strong>{{ formatAmount(item.share) }}</strong><span class="unit">{{ item.isTtc ? 'TTC' : 'HT' }}</span>
          </div>
          <div class="purchase-actions">
            <button type="button" class="row-button" @click="$refs.purchaseModal.openModal(item.purchase)"><span class="fas fa-up-right-from-square"></span>Ouvrir</button>
            <button v-if="canPay(item.purchase)" type="button" class="row-button" @click="pay(item.purchase)"><span class="fas fa-money-bill-transfer"></span>{{ isCreditNote(item.purchase) ? 'Rembourser' : 'Payer' }}</button>
            <button type="button" class="row-button danger" title="Retirer cet achat du dossier (l'achat n'est pas supprimé)" @click="detach(item.purchase)"><span class="fas fa-link-slash"></span>Détacher</button>
          </div>
        </div>
      </div>
    </article>
  </section>

  <LinkPurchaseModal ref="linkPurchaseModal" :callback="refreshCallback" />
  <AssignArticlesModal ref="assignArticlesModal" :callback="refreshCallback" />
  <PurchaseModal ref="purchaseModal" :callback="refreshCallback" />
  <DisbursementModal ref="disbursementModal" :callback="refreshCallback" />
  <ConfirmModal ref="detachConfirmModal" title="Détacher l'achat" text="Retirer cet achat du dossier ? L'achat est conservé, seule sa ventilation sur ce dossier est supprimée." yes="Détacher" no="Retour"/>
  <ConfirmModal ref="removeArticleConfirmModal" title="Retirer l'article" text="Retirer cet article du dossier ? Ses quantités retournent en stock, les achats sont conservés." yes="Retirer" no="Retour"/>
</template>

<script>
import {library} from '@fortawesome/fontawesome-svg-core';
import {faBoxesStacked, faCartShopping, faHourglassHalf, faLink, faLinkSlash, faMoneyBillTransfer, faPlus, faTriangleExclamation, faUpRightFromSquare} from "@fortawesome/free-solid-svg-icons";
import {NotificationService} from "@vues/v-common";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import ConfirmModal from "@/components/modals/confirmModal.vue";
import PurchaseModal from "@/purchase/components/modals/PurchaseModal.vue";
import DisbursementModal from "@/purchase/components/modals/DisbursementModal.vue";
import LinkPurchaseModal from "@/components/production/LinkPurchaseModal.vue";
import AssignArticlesModal from "@/purchase/components/modals/AssignArticlesModal.vue";
import {ArticleController} from "@/purchase/controllers/ArticleController";
import {LogisticsController} from "@/purchase/controllers/LogisticsController";
import {PurchaseController} from "@/purchase/controllers/PurchaseController";
import {PURCHASE_STATUS_ENUM, formatAmount, formatDate, formatQuantity, isCreditNote, statusEnumOf} from "@/purchase/utils";
import {toAllocationPayload} from "@/purchase/allocations";
library.add(faBoxesStacked, faCartShopping, faHourglassHalf, faLink, faLinkSlash, faMoneyBillTransfer, faPlus, faTriangleExclamation, faUpRightFromSquare);

// Panneau « Achats » d'un dossier : les achats qui y sont ventilés, avec la part du dossier, le statut et le reste à payer.
// `items` vient de purchasesOfOrder, `purchases` est la liste de tous les achats (pour en rattacher un autre).
// Un achat se rattache de deux façons : en entier (LinkPurchaseModal : services, facture complète) ou par articles du catalogue
// (AssignArticlesModal : quantités prélevées sur le stock). Les articles du dossier sont listés à part : c'est une lecture des mêmes achats.
// refreshCallback recharge la vue après chaque modification (rattachement, paiement, achat modifié).
export default {
  name: 'OrderPurchases',
  components: {StatusBadge, ConfirmModal, PurchaseModal, DisbursementModal, LinkPurchaseModal, AssignArticlesModal},
  props: ['order', 'items', 'finance', 'purchases', 'refreshCallback'],
  data() {
    return {
      PURCHASE_STATUS_ENUM,
      orderArticles: [],     // articles du catalogue affectés à ce dossier : {articleId, name, unit, quantity, costHt}
    };
  },
  mounted() {
    this.loadOrderArticles();
  },
  watch: {
    // Les achats du dossier sont rechargés après chaque modification, ou quand on change de dossier
    items() {
      this.loadOrderArticles();
    },
  },
  methods: {
    formatAmount,
    formatDate,
    formatQuantity,
    isCreditNote,
    statusEnumOf,
    purchaseTitle(purchase) {
      return [purchase.reference, purchase.label].filter((part, index, parts) => part && parts.indexOf(part) === index).join(' - ');
    },
    canPay(purchase) {
      return purchase.status !== PURCHASE_STATUS_ENUM.TO_REVIEW.value && purchase.remainingAmount > 0;
    },
    pay(purchase) {
      this.$refs.disbursementModal.openModal({purchase});
    },
    // Ce que le dossier a pris de chaque article du catalogue, du plus coûteux au moins coûteux
    async loadOrderArticles() {
      const orderId = String(this.order.id);
      const stats = await ArticleController.getStats();
      if (String(this.order.id) !== orderId) return;   // un autre dossier a été sélectionné entre-temps

      this.orderArticles = (stats && !stats.errors ? stats : [])
          .map(article => ({article, share: article.byOrder.find(order => String(order.orderId) === orderId)}))
          .filter(({share}) => share && (share.quantity > 0 || share.pendingQuantity > 0))
          .map(({article, share}) => ({articleId: article.articleId, name: article.name, unit: article.unit, quantity: share.quantity, pendingQuantity: share.pendingQuantity, costHt: share.costHt}))
          .sort((a, b) => b.costHt - a.costHt);
    },
    async removeArticle(entry) {
      const res = await this.$refs.removeArticleConfirmModal.displayChoice(() => LogisticsController.unassignArticle({orderId: this.order.id, articleId: entry.articleId}));
      if (res !== false && !(res && res.errors)) {
        (new NotificationService).addNotification("success", "Article retiré du dossier");
        this.refreshCallback();
      }
    },
    // Retire seulement les parts de ce dossier : l'achat et ses autres dossiers sont conservés
    async detach(purchase) {
      const allocations = (purchase.allocations ?? [])
          .filter(allocation => String(allocation.orderId) !== String(this.order.id))
          .map(toAllocationPayload);
      const res = await this.$refs.detachConfirmModal.displayChoice(() => PurchaseController.patch(purchase.id, {allocations}));
      if (res !== false && !(res && res.errors)) {
        (new NotificationService).addNotification("success", "Achat détaché du dossier");
        this.refreshCallback();
      }
    },
  },
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/components/production/panel.styl';

.head-actions
  display flex
  flex-wrap wrap
  gap 8px
  margin-left auto

.head-button
  display flex
  align-items center
  gap 7px
  height 32px
  padding 0 12px
  border 1px solid #cdd6ea
  border-radius 9px
  background white
  color THEME_COLOR_PRIMARY
  font-size 13px
  font-weight 600
  cursor pointer
  transition all .15s ease

  &:hover
    background #eef2fb

  &.primary
    border-color THEME_COLOR_PRIMARY
    background THEME_COLOR_PRIMARY
    color THEME_COLOR_PRIMARY_OPPOSITE

    &:hover
      background CTRL_PRIMARY_HOVER

  span
    font-size 12px

.purchase
  margin-bottom 6px
  padding 8px 12px
  border 1px solid #e6eaf2
  border-left-width 4px
  border-left-color #9db0dd
  border-radius 12px
  background #fbfcfe
  transition box-shadow .15s ease

  &:hover
    box-shadow 0 2px 8px rgba(36, 57, 107, 0.08)

  &.review
    border-left-color #e6b800
    background #fffdf3

  &.overdue
    border-left-color #d9534f

.purchase-body
  display flex
  align-items flex-start
  gap 12px

.purchase-info
  flex 1
  min-width 0

.purchase-side
  display flex
  flex-direction column
  align-items flex-end
  gap 6px
  flex 0 0 auto

.purchase-id
  display flex
  align-items baseline
  gap 10px
  min-width 0

  .supplier
    flex 0 0 auto
    max-width 45%
    overflow hidden
    white-space nowrap
    text-overflow ellipsis
    color FONT_COLOR

  .purchase-label
    min-width 0
    overflow hidden
    white-space nowrap
    text-overflow ellipsis
    font-size 13.5px
    color #6f7a93

.purchase-share
  display flex
  align-items baseline
  gap 4px
  white-space nowrap

  strong
    font-size 16px

  .unit
    font-size 11px
    font-weight 700
    color #8a93a8

.purchase-meta
  display flex
  align-items center
  flex-wrap wrap
  gap 4px 12px
  margin-top 4px

  .meta
    display flex
    align-items center
    gap 6px
    font-size 12.5px
    color #6f7a93

    span
      font-size 11px

    &.partial
      font-weight 600
      color #4a5670

    &.late
      font-weight 600
      color #c0392b

    &.warn
      color #9a6b00

.purchase-actions
  display flex
  flex-wrap wrap
  justify-content flex-end
  gap 4px

.row-button
  display flex
  align-items center
  gap 6px
  height 28px
  padding 0 10px
  border 1px solid #e0e5f0
  border-radius 8px
  background white
  color #4a5670
  font-size 12.5px
  font-weight 600
  cursor pointer
  transition all .15s ease

  &:hover
    background #eef2fb
    border-color #c5d0ea
    color THEME_COLOR_PRIMARY

  &.danger:hover
    background #fcebea
    border-color #efc4c0
    color #c0392b

  span
    font-size 11px

.order-articles
  margin-bottom 10px
  padding 8px 12px
  border 1px solid #e3e7ef
  border-radius 10px
  background #fbfcfe

  .order-articles-head
    display flex
    flex-wrap wrap
    align-items baseline
    gap 2px 10px
    margin-bottom 4px

    strong
      font-size 13.5px
      color FONT_COLOR

    span
      font-size 12px
      color #8a93a8

  ul
    margin 0
    padding 0
    list-style none

  li
    display grid
    grid-template-columns minmax(0, 1fr) auto auto auto
    align-items center
    gap 14px
    padding 4px 0
    font-size 13.5px

    & + li
      border-top 1px solid #eef1f7

  .article-name
    overflow hidden
    white-space nowrap
    text-overflow ellipsis

  .article-quantity, .article-cost
    text-align right
    font-variant-numeric tabular-nums

  .pending
    display block
    font-size 11.5px
    font-weight 600
    color #9a6b00

  .article-cost
    min-width 90px
    color #6f7a93

.panel-total
  display flex
  justify-content space-between
  align-items baseline
  margin-bottom 10px
  padding 8px 12px
  border-radius 10px
  background #eef2fb
  font-size 14px
  color #5f6b85

  strong
    font-size 16px
    color FONT_COLOR

</style>
